import { useState, useMemo } from 'react'
import ReactECharts from 'echarts-for-react'

const CHANNEL_COLORS: Record<string, string> = {
  'Direct': '#6366F1',
  'Organic Search': '#10B981',
  'Referral': '#F59E0B',
  'Paid Search': '#8B5CF6',
  'Organic Social': '#EC4899',
  'Paid Social': '#14B8A6',
  'Email': '#F97316',
  'Display': '#06B6D4',
  'AI': '#A855F7',
}
const DEFAULT_COLOR = '#94A3B8'

const METRIC_TABS = [
  { key: 'sessions', label: 'Sessions' },
  { key: 'totalUsers', label: 'Utilisateurs' },
  { key: 'screenPageViews', label: 'Pages vues' },
] as const

interface ChannelRow {
  channel: string
  sessions: number
  totalUsers?: number
  screenPageViews?: number
}

interface ChannelDistributionChartProps {
  data: ChannelRow[]
}

export function ChannelDistributionChart({ data }: ChannelDistributionChartProps) {
  const [activeMetric, setActiveMetric] = useState<string>('sessions')

  const option = useMemo(() => {
    const channels = data.map((d) => d.channel)
    const values = data.map((d) => (d as any)[activeMetric] ?? d.sessions ?? 0)
    const colors = data.map((d) => CHANNEL_COLORS[d.channel] ?? DEFAULT_COLOR)

    return {
      grid: { left: 56, right: 16, top: 16, bottom: 60 },
      xAxis: {
        type: 'category' as const,
        data: channels,
        axisLabel: {
          rotate: channels.length > 5 ? 30 : 0,
          color: '#8B8FA7',
          fontSize: 11,
        },
        axisLine: { lineStyle: { color: '#E2E4ED' } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value' as const,
        axisLabel: {
          color: '#8B8FA7',
          fontSize: 11,
          formatter: (v: number) => (v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : String(v)),
        },
        splitLine: { lineStyle: { color: '#E2E4ED', type: 'dashed' as const } },
      },
      tooltip: {
        trigger: 'item' as const,
        backgroundColor: '#1B2559',
        borderColor: '#1B2559',
        borderRadius: 8,
        textStyle: { color: '#fff', fontSize: 12 },
        formatter: (params: any) => {
          const label = METRIC_TABS.find(t => t.key === activeMetric)?.label ?? 'Sessions'
          return `<div style="font-size:12px"><strong>${params.name}</strong><br/>${label} : ${params.value?.toLocaleString('fr-FR')}</div>`
        },
      },
      series: [
        {
          type: 'bar' as const,
          barWidth: '60%',
          data: values.map((value: number, i: number) => ({
            value,
            itemStyle: { color: colors[i] },
          })),
        },
      ],
    }
  }, [data, activeMetric])

  return (
    <div className="bg-white border border-[#E2E4ED] rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-[#1B2559]">R&eacute;partition des canaux</h3>
        <div className="flex gap-1">
          {METRIC_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveMetric(tab.key)}
              className={`text-[11px] px-2.5 py-1 rounded-md font-medium transition-colors ${
                activeMetric === tab.key
                  ? 'bg-[#6366F1] text-white'
                  : 'text-[#8B8FA7] hover:text-[#555A73] hover:bg-[#F4F5FA]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      <ReactECharts option={option} style={{ height: 260 }} notMerge />
    </div>
  )
}
